/*
 * ॐ नमः शिवाय
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * PropertiesDrawer — fullscreen overlay when a section is selected.
 * Split-pane: left (~45%) = live preview | right (~55%) = settings panel.
 * Follows the same UI pattern as DesignTokenEditor (purple gradient header).
 */

import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTheme } from '../../theme/ThemeContext';
import APIHelper from '../../api/APIHelper';
import envConfig from '../../config/envConfig';
import SectionLivePreview from './SectionLivePreview';
import ResponsiveControls from './ResponsiveControls';
import HeroEditor from './editors/HeroEditor';
import TextBlockEditor from './editors/TextBlockEditor';
import ImageBannerEditor from './editors/ImageBannerEditor';
import DividerEditor from './editors/DividerEditor';
import DataCardsEditor from './editors/DataCardsEditor';
import DataTableEditor from './editors/DataTableEditor';
import DataListEditor from './editors/DataListEditor';
import DataTimelineEditor from './editors/DataTimelineEditor';
import ChartEditor from './editors/ChartEditor';
import StatWidgetsEditor from './editors/StatWidgetsEditor';
import FormBuilderEditor from './editors/FormBuilderEditor';
import HtmlCssEditor from './editors/HtmlCssEditor';
import PublicPageEditor from './editors/PublicPageEditor';
import ComponentEditor from './editors/ComponentEditor';

const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

const EDITOR_MAP = {
  hero:               HeroEditor,
  text_block:         TextBlockEditor,
  image_banner:       ImageBannerEditor,
  divider:            DividerEditor,
  data_cards:         DataCardsEditor,
  data_table:         DataTableEditor,
  data_list:          DataListEditor,
  data_timeline:      DataTimelineEditor,
  chart_bar:          ChartEditor,
  chart_line:         ChartEditor,
  stat_widgets:       StatWidgetsEditor,
  form_builder:       FormBuilderEditor,
  html_css_workspace: HtmlCssEditor,
  public_page:        PublicPageEditor,
  component:          ComponentEditor,
};

const SECTION_LABELS = {
  component:          'Component',
  hero:               'Hero',
  text_block:         'Text Block',
  image_banner:       'Image Banner',
  divider:            'Divider',
  data_cards:         'Data Cards',
  data_table:         'Data Table',
  data_list:          'Data List',
  data_timeline:      'Data Timeline',
  chart_bar:          'Bar Chart',
  chart_line:         'Line Chart',
  stat_widgets:       'Stat Widgets',
  form_builder:       'Form Builder',
  html_css_workspace: 'HTML/CSS/JS Workspace',
  public_page:        'Public Page',
};

const EXCLUDE_RESPONSIVE = new Set(['html_css_workspace']);

export default function PropertiesDrawer({ section, dispatch, appId, allSections, pageVisibility, onClose, onSave, isSaving }) {
  const { theme } = useTheme();
  const queryClient = useQueryClient();
  const isOpen = Boolean(section);
  const [savingTemplate, setSavingTemplate]     = useState(false);
  const [templateName, setTemplateName]         = useState('');
  const [showTemplateSave, setShowTemplateSave] = useState(false);

  const handleSaveTemplate = async () => {
    if (!templateName.trim() || !section) return;
    setSavingTemplate(true);
    try {
      await apiHelper.apiCall('/arcee/application/section-template/save/', {
        method: 'POST',
        body: JSON.stringify({
          application_id: parseInt(appId),
          name: templateName.trim(),
          section_type: section.section_type,
          default_config: {
            section_config: section.section_config,
            layout_config:  section.layout_config,
            data_binding:   section.data_binding,
          },
        }),
      });
      queryClient.invalidateQueries({ queryKey: ['sectionTemplates', appId] });
      setShowTemplateSave(false);
      setTemplateName('');
    } catch (_) {
      // non-critical
    } finally {
      setSavingTemplate(false);
    }
  };

  const EditorComponent = section ? EDITOR_MAP[section.section_type] : null;
  const label = section ? (SECTION_LABELS[section.section_type] || section.section_type) : '';

  const showResponsive = section && !EXCLUDE_RESPONSIVE.has(section.section_type);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1040,
        display: 'flex',
        flexDirection: 'column',
        background: '#f1f5f9',
      }}
    >
      {/* ── Header — purple gradient (matches DesignTokenEditor) ── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '0 20px',
          height: 62,
          flexShrink: 0,
          background: theme.gradientBackground,
          boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
        }}
      >
        {/* Close / Back */}
        <button
          type="button"
          onClick={onClose}
          style={{
            background: 'rgba(255,255,255,0.12)',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: 8,
            color: '#fff',
            fontSize: 13,
            fontWeight: 600,
            padding: '5px 12px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexShrink: 0,
          }}
          title="Close properties"
        >
          <i className="bi bi-arrow-left" style={{ fontSize: 13 }} />
          Back
        </button>

        {/* Section label */}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Section Properties
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', marginTop: 1 }}>
            {label}
          </div>
        </div>

        {/* Save button */}
        <button
          type="button"
          onClick={onSave}
          disabled={isSaving}
          style={{
            background: isSaving ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.18)',
            border: '1px solid rgba(255,255,255,0.28)',
            borderRadius: 8,
            color: '#fff',
            fontSize: 13,
            fontWeight: 600,
            padding: '5px 16px',
            cursor: isSaving ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexShrink: 0,
          }}
          title="Save draft (Ctrl+S)"
        >
          {isSaving
            ? <><span className="spinner-border spinner-border-sm" style={{ width: 13, height: 13 }} /> Saving…</>
            : <><i className="bi bi-floppy" style={{ fontSize: 13 }} /> Save</>
          }
        </button>
      </div>

      {/* ── Body — split pane ── */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* ── Left pane: Live Preview (45%) ── */}
        <div
          style={{
            width: '45%',
            flexShrink: 0,
            background: '#e8edf4',
            borderRight: '1px solid #d1d9e4',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <SectionLivePreview section={section} />
        </div>

        {/* ── Right pane: Settings (55%) ── */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            background: '#fff',
            overflow: 'hidden',
          }}
        >

          {/* Scrollable editor content */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
            {EditorComponent ? (
              <EditorComponent section={section} dispatch={dispatch} appId={appId} allSections={allSections} pageVisibility={pageVisibility} />
            ) : (
              <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', marginTop: 60 }}>
                No editor available for <strong>{section.section_type}</strong>.
              </div>
            )}
          </div>

          {/* Footer — Save as Template */}
          <div
            style={{
              padding: '12px 20px',
              borderTop: '1px solid #e2e8f0',
              flexShrink: 0,
              background: '#f8fafc',
            }}
          >
            {showTemplateSave ? (
              <div>
                <div style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                  <i className="bi bi-bookmark me-1" />Save Section as Template
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input
                    className="form-control form-control-sm"
                    placeholder="Template name…"
                    value={templateName}
                    onChange={e => setTemplateName(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleSaveTemplate();
                      if (e.key === 'Escape') { setShowTemplateSave(false); setTemplateName(''); }
                    }}
                    autoFocus
                    style={{ flex: 1, fontSize: 12 }}
                  />
                  <button
                    type="button"
                    className="btn btn-sm"
                    style={{ background: theme.primary, color: '#fff', border: 'none', fontSize: 11, whiteSpace: 'nowrap' }}
                    onClick={handleSaveTemplate}
                    disabled={savingTemplate || !templateName.trim()}
                  >
                    {savingTemplate ? <span className="spinner-border spinner-border-sm" /> : 'Save'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    style={{ fontSize: 11 }}
                    onClick={() => { setShowTemplateSave(false); setTemplateName(''); }}
                  >
                    ✕
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary w-100"
                style={{ fontSize: 11 }}
                onClick={() => { setTemplateName(''); setShowTemplateSave(true); }}
                title="Save this section's config as a reusable template"
              >
                <i className="bi bi-bookmark-plus me-1" />
                Save Section as Template
              </button>
            )}
          </div>

          {/* Bottom accent bar */}
          <div style={{ height: 3, background: theme.gradientBackground, flexShrink: 0 }} />
        </div>
      </div>
    </div>
  );
}
